import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ShoppingBag } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { cartService } from '@/services/cart.service';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/utils';
import toast from '@/hooks/useToast';

export const CartPage = () => {
  const { cart, setCart } = useCartStore();
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [useLoyalty, setUseLoyalty] = useState(false);

  useEffect(() => {
    cartService.getCart().then(res => setCart(res.data.data));
  }, []);

  const handleUpdateQty = async (id: string, qty: number) => {
    try {
      const res = await cartService.updateItem(id, qty);
      setCart(res.data.data);
    } catch { toast.error('Could not update quantity'); }
  };

  const handleRemove = async (id: string) => {
    try {
      await cartService.removeItem(id);
      const res = await cartService.getCart();
      setCart(res.data.data);
    } catch { toast.error('Could not remove item'); }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const res = await api.post('/coupons/apply', { code: couponCode, cartTotal: subtotal });
      setCouponDiscount(res.data.data.discount);
      setAppliedCoupon(couponCode.toUpperCase());
      toast.success(`Coupon applied! You save ${formatCurrency(res.data.data.discount)}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Invalid coupon';
      toast.error(msg);
    } finally { setApplyingCoupon(false); }
  };

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <ShoppingBag className="h-20 w-20 text-gray-200 mx-auto mb-6" />
        <h2 className="font-heading font-bold text-2xl text-gray-800 mb-3">Your cart is empty</h2>
        <p className="text-gray-500 mb-8">Looks like you haven't added anything yet.</p>
        <Button asChild>
          <Link to="/categories/medicines">Start Shopping</Link>
        </Button>
      </div>
    );
  }

  const subtotal = cart.subtotal;
  const loyaltyDiscount = useLoyalty && user ? Math.floor(Math.min(user.loyaltyPoints, subtotal) / 100) * 10 : 0;
  const totalDiscount = couponDiscount + loyaltyDiscount;
  const deliveryCharge = subtotal - totalDiscount < 500 ? 50 : 0;
  const total = subtotal - totalDiscount + deliveryCharge;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="font-heading font-bold text-2xl text-gray-800 mb-8">Shopping Cart ({cart.items.length} items)</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Cart items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map(item => (
            <div key={item.id} className="bg-white rounded-card shadow-card p-5 flex gap-4">
              <Link to={`/products/${item.product.slug}`}>
                <img src={item.product.images[0] || '/placeholder-product.jpg'} alt={item.product.name} className="w-24 h-24 object-cover rounded-xl" />
              </Link>
              <div className="flex-1">
                <Link to={`/products/${item.product.slug}`}>
                  <h3 className="font-semibold text-gray-800 hover:text-primary-600 transition-colors">{item.product.name}</h3>
                </Link>
                {item.product.brand && <p className="text-xs text-gray-400 mt-0.5">{item.product.brand.name}</p>}
                {item.product.requiresPrescription && <span className="badge-rx inline-block mt-1">Rx</span>}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden">
                    <button onClick={() => handleUpdateQty(item.id, item.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-gray-50">
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-10 text-center font-semibold">{item.quantity}</span>
                    <button onClick={() => handleUpdateQty(item.id, item.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-gray-50">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary-600">{formatCurrency(Number(item.product.discountedPrice || item.product.price) * item.quantity)}</p>
                    {item.product.discountedPrice && (
                      <p className="text-xs text-gray-400 line-through">{formatCurrency(Number(item.product.price) * item.quantity)}</p>
                    )}
                  </div>
                  <button onClick={() => handleRemove(item.id)} className="ml-3 text-gray-400 hover:text-error transition-colors">
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="space-y-4">
          {/* Coupon */}
          <div className="bg-white rounded-card shadow-card p-5">
            <h3 className="font-heading font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Tag className="h-4 w-4 text-primary-500" /> Coupon Code
            </h3>
            {appliedCoupon ? (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                <span className="text-green-700 text-sm font-semibold">{appliedCoupon} applied!</span>
                <button onClick={() => { setAppliedCoupon(''); setCouponDiscount(0); }} className="ml-auto text-green-500 text-xs">Remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" className="text-sm" />
                <Button size="sm" onClick={applyCoupon} loading={applyingCoupon} className="shrink-0">Apply</Button>
              </div>
            )}
          </div>

          {/* Loyalty */}
          {isAuthenticated && user && user.loyaltyPoints >= 100 && (
            <div className="bg-white rounded-card shadow-card p-5">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Use Milan Points</p>
                  <p className="text-xs text-gray-500">You have {user.loyaltyPoints} points (₹{Math.floor(user.loyaltyPoints / 100) * 10} off)</p>
                </div>
                <button
                  onClick={() => setUseLoyalty(!useLoyalty)}
                  className={`relative w-10 h-5 rounded-full transition-all ${useLoyalty ? 'bg-primary-500' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${useLoyalty ? 'translate-x-5' : ''}`} />
                </button>
              </label>
            </div>
          )}

          {/* Summary */}
          <div className="bg-white rounded-card shadow-card p-5">
            <h3 className="font-heading font-semibold text-gray-800 mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
              {couponDiscount > 0 && <div className="flex justify-between text-success"><span>Coupon ({appliedCoupon})</span><span>-{formatCurrency(couponDiscount)}</span></div>}
              {loyaltyDiscount > 0 && <div className="flex justify-between text-success"><span>Milan Points</span><span>-{formatCurrency(loyaltyDiscount)}</span></div>}
              <div className="flex justify-between"><span className="text-gray-500">Delivery</span><span>{deliveryCharge === 0 ? <span className="text-success">FREE</span> : formatCurrency(deliveryCharge)}</span></div>
              {deliveryCharge > 0 && <p className="text-xs text-gray-400">Add ₹{(500 - (subtotal - totalDiscount)).toFixed(0)} more for free delivery</p>}
              <div className="border-t pt-2 flex justify-between font-bold text-lg">
                <span>Total</span><span className="text-primary-600">{formatCurrency(total)}</span>
              </div>
            </div>
            <Button className="w-full mt-4" onClick={() => navigate('/checkout')}>
              Proceed to Checkout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
